// ────────────────────────────────────────────────────────────────────
// Gated — wraps a PREMIUM section. If the active tenant is entitled to the
// feature, render the children; otherwise show an honest lock card with an
// upgrade CTA (never a fake teaser value). This is UX only — the real boundary
// is require_feature() on the backend (premium endpoints return 402).
// ────────────────────────────────────────────────────────────────────
import type { JSX, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { apiClient } from "@/lib/api";

export function useEntitlements() {
  return useQuery({
    queryKey: ["entitlements"],
    queryFn: () => apiClient.entitlements(),
    staleTime: 5 * 60_000,
  });
}

/** Marks a section as a premium feature (shown whether locked or unlocked). */
export function PremiumBadge({ light }: { light?: boolean }): JSX.Element {
  return (
    <span className="font-mono uppercase shrink-0" style={{
      fontSize: 8, letterSpacing: ".12em", padding: "1px 4px",
      color: "var(--accent-deep)", border: "1px solid var(--accent)",
      opacity: light ? 0.9 : 1,
    }}>
      Premium
    </span>
  );
}

export function Gated({ feature, title, blurb, children }: {
  feature: string;
  title: string;
  blurb: string;
  children: ReactNode;
}): JSX.Element {
  const { data, isPending } = useEntitlements();
  // Fail closed: until we know, don't reveal premium content.
  const entitled = !!data?.features.includes(feature);
  if (entitled) return <>{children}</>;

  return (
    <div className="relative overflow-hidden"
         style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
      <div className="px-4 py-4 flex items-start gap-3">
        <div className="w-8 h-8 shrink-0 flex items-center justify-center"
             style={{ border: "1px solid var(--hairline)", background: "var(--paper)" }}>
          <Lock size={14} style={{ color: "var(--accent)" }} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-sans font-medium" style={{ fontSize: 13.5, color: "var(--ink-1)" }}>{title}</span>
            <span className="font-mono uppercase px-1.5 py-px" style={{ fontSize: 8, letterSpacing: ".12em", color: "var(--accent-deep)", border: "1px solid var(--accent)" }}>Premium</span>
          </div>
          <p className="font-sans mt-1" style={{ fontSize: 12, lineHeight: 1.5, color: "var(--ink-3)" }}>{blurb}</p>
          <button
            disabled={isPending}
            className="mt-2.5 font-mono uppercase px-3 py-1.5 transition-colors"
            style={{ fontSize: 9, letterSpacing: ".16em", color: "var(--ink-1)", background: "var(--accent)" }}
            title="Premium features are enabled by your XZYMODS account manager"
          >
            Upgrade to unlock
          </button>
        </div>
      </div>
    </div>
  );
}
